const asString = (value, defaultValue = "") =>
    typeof value === "string" ? value : defaultValue;

const asNumber = (value, defaultValue = 0) => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === "string") {
    const parsed = Number(value.trim());
    return Number.isFinite(parsed) ? parsed : defaultValue;
  }

  return defaultValue;
};

const asBoolean = (value) => value === true;

const asArray = (value) => (Array.isArray(value) ? value : []);

const asNullableString = (value) =>
    typeof value === "string" ? value : null;

const asNullableValue = (value) =>
    value === undefined ? null : value;

const parseJsonValue = (value, defaultValue = null) => {
    if (value === null || value === undefined || value === "") {
        return defaultValue;
    }

    if (typeof value === "object") {
        return value;
    }

    if (typeof value !== "string") {
        return defaultValue;
    }

    try {
        return JSON.parse(value);
    } catch {
        return defaultValue;
    }
};

export function normalizeCustomFieldOption(option = {}) {
    return {
        Item1: asString(option.key),
        Item2: asString(option.value),
    };
}

export function normalizeCustomField(field = {}) {
    const options = Array.isArray(field.items)
        ? field.items.map(normalizeCustomFieldOption)
        : null;

    return {
        TemplateId: asNumber(field.template_id || field.templateId),
        FieldGroup: asString(field.field_group_name || field.fieldGroupName),
        Value: asNullableValue(field.value),
        Name: asString(field.field_name || field.fieldName),
        isSystemField: asBoolean(field.is_system_field || field.isSystemField),
        FieldType: asString(field.field_type || field.fieldType),
        Options: options,
        Description: asNullableString(field.description),
        Label: asString(field.label),
        IsActive: asBoolean(field.is_active || field.isActive),
        FieldKey: asString(field.field_key || field.fieldKey),
        IsCopyDestination: asBoolean(field.is_copy_destination || field.isCopyDestination),
        IsCopySource: asBoolean(field.is_copy_source || field.isCopySource),
        Group: asString(field.group),
        Sequence: asNumber(field.sequence),
        CustomFieldId: asNumber(field.custom_field_id || field.customFieldId),
        IsRequired: asBoolean(field.is_required || field.isRequired),
        CopyToField: asNullableValue(field.copy_to_field || field.copyToField),
        IsSelected: asBoolean(field.is_selected || field.isSelected),
        MaxLength: asNumber(field.max_length || field.maxLength),
        PreInitScript: asNullableString(field.pre_init_script || field.preInitScript),
        PreSaveScript: asNullableString(field.pre_save_script || field.preSaveScript),
        ValidationJson: asNullableValue(field.validation_json || field.validationJson),
        IsNonEditable: asBoolean(field.is_non_editable || field.isNonEditable),
        FieldSettings: asNullableString(field.field_settings || field.fieldSettings || field.settings || null),
        FieldSettingConfig: asNullableValue(field.field_setting_config || field.fieldSettingConfig),
        ParentDetail: asNullableValue(field.parent_detail || field.parentDetail),
        ExternalSourceId: asNumber(field.external_source_id || field.externalSourceId),
        CopyFromFieldValue: asNullableValue(field.copy_from_field_value || field.copyFromFieldValue),
        CopyFromField: asNullableValue(field.copy_from_field || field.copyFromField),
    };
}

const asPinnedFields = (value) =>
    asArray(value).map((item) => ({
        seq_no: asNumber(item.seq_no || item.seqNo),
        cf_key: asString(item.cf_key || item.cfKey),
    }));

const STATUS_DISPLAY_FALLBACK = {
    reopen: 'Reopened',
};

export function normalizeWorkkard(workkard = {}) {
    const workkardStatus = asString(workkard.workkard_status || workkard.workkadStatus);
    const statusDisplayFallback = STATUS_DISPLAY_FALLBACK[workkardStatus.toLowerCase()] || workkardStatus;
    return {
        Id: asNumber(workkard.id),
        WorkkardNumber: asString(workkard.workkard_number || workkard.workkardNumber),
        WorkkardTitle: asString(workkard.workkard_title || workkard.workkardTitle),
        WorkkardSummaryFull: asString(workkard.workkard_summary_full || workkard.workkardSummaryFull),
        WorkkardStatus: workkardStatus,
        WorkkardStatusForDisplay: asString(workkard.workkard_status_for_display || workkard.workkardStatusForDisplay) || statusDisplayFallback,
        WorkkardReopenReason: asString(workkard.workkard_reopen_reason || workkard.workkardReopenReason),
        WorkkardReopenSubReason: asString(workkard.workkard_reopen_sub_reason || workkard.workkardReopenSubReason),
        LitClaimStatusForWorkkard: asString(workkard.lit_claim_status_for_workkard || workkard.litClaimStatusForWorkkard),
        Sponsor: asNumber(workkard.sponsor),
        SponsorName: asString(workkard.sponsor_name || workkard.sponsorName),
        SponsorImage: asString(workkard.sponsor_image || workkard.sponsorImage),
        CarrierId: asNumber(workkard.carrier_id || workkard.carrierId),
        CarrierName: asString(workkard.carrier_name || workkard.carrierName),
        CarrierNameAbbr: asString(workkard.carrier_name_abbr || workkard.carrierNameAbbr),
        TemplateId: asNumber(workkard.template_id || workkard.templateId),
        TemplateName: asString(workkard.template_name || workkard.templateName),
        TemplateShortName: asString(workkard.template_short_name || workkard.templateShortName),
        CreatedBy: asNumber(workkard.created_by || workkard.createdBy),
        OrganizationId: asNumber(workkard.organization_id || workkard.organizationId),
        CreatedDate: asNumber(workkard.created_date || workkard.createdDate),
        CreatedDateString: asString(workkard.created_date_string || workkard.createdDateString),
        IsSysUserCreated: asBoolean(workkard.is_sys_user_created || workkard.isSysUserCreated),
        IsCurrentOrgInvolved: asBoolean(workkard.is_current_org_involved || workkard.isCurrentOrgInvolved),
        IsClaimWorkkard: asBoolean(workkard.is_claim_workkard || workkard.isClaimWorkkard),
        WorkkardFiles: asArray(workkard.workkard_files || workkard.workkardFiles),
        WorkkardFileCount: asNumber(workkard.workkard_file_count || workkard.workkardFileCount),
        CustomFields: asArray(workkard.custom_fields || workkard.customFields).map(normalizeCustomField),
        pinned_fields: asPinnedFields(workkard.pinned_fields || workkard.pinnedFields),
        IsPinnedCustomfields: workkard.pinned_fields && workkard.pinned_fields.length > 0,
        ActionMenu:[]
    };
}